import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Loader2 } from "lucide-react";

import { BRAND } from "../components/terminal/BrandLogo";
const BULL = "https://static.prod-images.emergentagent.com/jobs/cfee5ed3-952d-485c-8c92-561a2e6c4885/images/e72bac7532b37f64eee874182a2cf6172ae29f3d2bb3ea1d3f339c87f77fd161.jpeg";

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen w-full flex bg-background text-foreground dark">
      <div className="hidden lg:flex flex-col justify-between w-[42%] p-10 border-r border-border relative overflow-hidden" style={{ background: "#0B0E14" }}>
        <div className="flex items-center gap-3" data-testid="auth-brand">
          <img src={BRAND.dark} alt={`${BRAND.name} — ${BRAND.tagline}`} className="h-14 w-auto object-contain" draggable={false} />
        </div>
        <img src={BULL} alt="bull" className="absolute right-[-40px] bottom-0 w-[420px] opacity-90 pointer-events-none" />
        <div className="relative z-10 max-w-xs">
          <h2 className="font-heading text-3xl font-bold leading-tight">Your own TradingView-grade chart engine.</h2>
          <p className="text-sm text-muted-foreground mt-3">Live Deriv tick streams · tick-native charts · Auto-BOS market structure · paper trading.</p>
          <div className="flex gap-2 mt-6">
            <span className="text-[10px] font-bold text-primary tracking-wide">DISCIPLINE</span>
            <span className="text-[10px] font-bold text-primary tracking-wide">EXECUTION</span>
            <span className="text-[10px] font-bold text-primary tracking-wide">FREEDOM</span>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h1 className="font-heading text-2xl font-bold">{title}</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await login(email, password); navigate("/"); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your Fela R.I.O terminal.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <Field label="Email" value={email} onChange={setEmail} type="email" testid="login-email" />
        <Field label="Password" value={password} onChange={setPassword} type="password" testid="login-password" />
        {error && <p className="text-sm text-sell" data-testid="login-error">{error}</p>}
        <button data-testid="login-submit" disabled={loading} className="w-full h-11 rounded-lg bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
        </button>
        <p className="text-sm text-muted-foreground text-center">
          No account? <Link to="/register" className="text-primary font-semibold" data-testid="to-register">Create one</Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground uppercase tracking-wide">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        required
        autoComplete={type === "password" ? "current-password" : type === "email" ? "email" : "off"}
        placeholder={type === "email" ? "you@example.com" : type === "password" ? "••••••••" : ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-11 px-3 rounded-lg bg-background border border-border text-sm outline-none focus:border-primary/60"
      />
    </div>
  );
}
